// hooks/useFetchReviewTemplates.ts
import { useCallback, useEffect, useState } from 'react';
import { getAPI } from '../../services/api.service';
import { toast } from 'react-toastify';

export type AssignmentItem = {
  _id: string;
  title?: string;
  groupId?: string;
  status?: string;
  dueAt?: string | null;
  visibleFrom?: string | null;
  visibleTo?: string | null;
};

export type ReviewTemplateItem = {
  _id: string;
  title: string;
  description?: string;
  createdBy?: any;
  tags?: string[];
  sections?: any;
  assignments?: AssignmentItem[];
};

export const useFetchReviewTemplates = (
  userId: string,
  role: string,
  status?: 'pre' | 'active' | 'post' | 'all',
  page = 1,
  limit = 12
) => {
  const [data, setData] = useState<ReviewTemplateItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async (p = page, l = limit, s = status) => {
    if (!userId) {
      setData([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Build query object - follow the same signature as your backend expects
      const params: any = {
        userId,
        page: String(p),
        limit: String(l)
      };
      if (s && s !== 'all') params.status = s;

      const res = await getAPI<{ data?: ReviewTemplateItem[]; total?: number }>(
        'book-review/',
        params
      );

      if (res?.status === 200 && res.data) {
        // backend returns { status:200, data: templates, total, ... } or similar
        // adapt to either shape:
        const payload = res.data;
        // if controller returns { status, data, total } shape:
        if ((payload as any).data !== undefined) {
          setData((payload as any).data || []);
          setTotal((payload as any).total || 0);
        } else {
          // fallback: assume payload is the array and res.data.total returned separately
          setData((payload as any)?.data || (res.data as any) || []);
          setTotal((res.data as any)?.total || 0);
        }
      } else {
        // fallback for unexpected shapes
        const maybeData = res?.data as any;
        if (Array.isArray(maybeData)) {
          setData(maybeData);
          setTotal(0);
        } else {
          setData([]);
          setTotal(0);
        }
      }
    } catch (err: any) {
      console.error('fetch templates err', err);
      setError(err?.message || 'Failed to fetch templates');
      toast.error(err?.message || 'Failed to fetch templates');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTemplates(page, limit, status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, status, page, limit]);

  return {
    templates: data,
    total,
    loading,
    error,
    refetch: fetchTemplates
  };
};
